import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, User, Lock, Bus, Eye, EyeOff, Phone } from 'lucide-react';
import OtpLoginModal from './OtpLoginModal';

const LoginModal = ({ isOpen, onClose }) => {
  const { login, ROLES } = useAuth();
  const [formData, setFormData] = useState({
    role: ROLES.PASSENGER,
    username: '',
    password: '',
    busNumber: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // If Passenger is selected, show OTP modal instead
    if (formData.role === ROLES.PASSENGER) {
      setShowOtpModal(true);
      return;
    }

    // For Manager and Conductor, use password login
    if (!formData.username || !formData.password) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData);
      if (result.success) {
        onClose();
        // Reset form
        setFormData({
          role: ROLES.PASSENGER,
          username: '',
          password: '',
          busNumber: ''
        });
      } else {
        // Show user-friendly error message
        const errorMsg = result.error || 'Invalid username or password. Please check your credentials.';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Unable to connect to server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = (userData) => {
    setShowOtpModal(false);
    onClose();
    // Reset form
    setFormData({
      role: ROLES.PASSENGER,
      username: '',
      password: '',
      busNumber: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-lg transform transition-all sm:my-6 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-4 sm:px-5 lg:px-6 py-4 sm:py-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">Login to Nuwantha</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5 sm:w-5.5 sm:h-5.5 lg:w-6 lg:h-6" />
              </button>
            </div>
            <p className="text-blue-100 mt-1.5 text-xs sm:text-sm">Choose your role and sign in</p>
          </div>

          {/* Body */}
          <div className="px-4 sm:px-5 lg:px-6 py-4 sm:py-5 lg:py-6">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              {/* Role Selection */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Select Role
                </label>
                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: ROLES.MANAGER }))}
                    className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl border-2 transition-all ${
                      formData.role === ROLES.MANAGER
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="w-5 h-5 sm:w-5.5 sm:h-5.5 lg:w-6 lg:h-6 mx-auto mb-1 sm:mb-1.5" />
                    <span className="text-xs sm:text-sm font-medium">Manager</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: ROLES.PASSENGER }))}
                    className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl border-2 transition-all ${
                      formData.role === ROLES.PASSENGER
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="w-5 h-5 sm:w-5.5 sm:h-5.5 lg:w-6 lg:h-6 mx-auto mb-1 sm:mb-1.5" />
                    <span className="text-xs sm:text-sm font-medium">Passenger</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: ROLES.CONDUCTOR }))}
                    className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl border-2 transition-all ${
                      formData.role === ROLES.CONDUCTOR
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Bus className="w-5 h-5 sm:w-5.5 sm:h-5.5 lg:w-6 lg:h-6 mx-auto mb-1 sm:mb-1.5" />
                    <span className="text-xs sm:text-sm font-medium">Conductor</span>
                  </button>
                </div>
              </div>

              {/* Show different forms based on role */}
              {formData.role === ROLES.PASSENGER ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <Phone className="w-5 h-5 sm:w-5.5 sm:h-5.5 lg:w-6 lg:h-6 text-blue-600" />
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-blue-900">OTP Login Required</p>
                      <p className="text-[10px] sm:text-xs text-blue-700 mt-0.5">Passengers must use OTP login with mobile number</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowOtpModal(true)}
                    className="w-full mt-2.5 bg-blue-600 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Continue with OTP Login
                  </button>
                </div>
              ) : (
                <>
                  {/* Username */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                      {formData.role === ROLES.CONDUCTOR ? 'Conductor Username' : 'Username'}
                    </label>
                    <div className="relative">
                      <User className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-gray-400" />
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 lg:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder={formData.role === ROLES.CONDUCTOR ? 'Enter conductor username' : 'Enter username'}
                        required
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full pl-8 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-2.5 lg:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5" /> : <Eye className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Bus Number for Conductor */}
              {formData.role === ROLES.CONDUCTOR && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                    Bus Number
                  </label>
                  <div className="relative">
                    <Bus className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-gray-400" />
                    <input
                      type="text"
                      name="busNumber"
                      value={formData.busNumber}
                      onChange={handleInputChange}
                      className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 lg:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter bus number (e.g., NC 6055)"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
                  <p className="text-red-600 text-xs sm:text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button - Only show for Manager and Conductor */}
              {formData.role !== ROLES.PASSENGER && (
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-2 sm:py-2.5 lg:py-3 px-4 sm:px-5 lg:px-6 rounded-lg sm:rounded-xl font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center space-x-1.5 sm:space-x-2 text-sm"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5" />
                      <span>Sign In</span>
                    </>
                  )}
                </button>
              )}
            </form>

            {/* Info for Manager and Conductor */}
            {formData.role !== ROLES.PASSENGER && (
              <div className="mt-4 sm:mt-5 lg:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5">Login Information:</h4>
                <div className="text-[10px] sm:text-xs text-gray-600 space-y-0.5">
                  <p><strong>{formData.role === ROLES.MANAGER ? 'Manager' : 'Conductor'}:</strong> Use your username and password to login</p>
                  <p className="text-gray-500 mt-1.5">Contact administrator if you forgot your password</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* OTP Login Modal for Passengers */}
      <OtpLoginModal 
        isOpen={showOtpModal} 
        onClose={() => setShowOtpModal(false)} 
        onSuccess={handleOtpSuccess}
      />
    </div>
  );
};

export default LoginModal;
